/** Local dev fixtures – used when dev bypass auth is active (no backend). */

export const MOCK_USER = {
  _id: 'dev-mock-user-id',
  email: 'dev.reviewer@resymaid.local',
  firstName: 'Nicolas',
  lastName: 'Barrionuevo',
  phone: '+1 (555) 010-2030',
  credits: 42,
  skills: ['JavaScript', 'Vue.js', 'TypeScript', 'Node.js', 'Python', 'AWS'],
  role: 'admin',
  isAdmin: true,
};

function daysAgo(n) {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString();
}

function matchScore(overall, verdict, categories, highlights, gaps) {
  return {
    overallScore: overall,
    overallVerdict: verdict,
    categories,
    highlights: highlights || [],
    gaps: gaps || [],
  };
}

/** Sample jobs shaped like the real API (array returned from GET /api/jobs). */
export function createMockJobs() {
  return [
    {
      _id: 'dev-job-001',
      title: 'Senior Frontend Engineer',
      companyName: 'Stripe',
      location: 'San Francisco, CA (Remote)',
      description: 'Build polished dashboard experiences with Vue and TypeScript.',
      url: 'https://example.com/jobs/stripe-senior-fe',
      sourceSite: 'LinkedIn',
      createdAt: daysAgo(1),
      status: 'saved',
      hasResume: false,
      salary: '$165K–$210K',
      jobType: 'Full-time',
      notes: '',
      compatibilityScore: matchScore(
        92,
        'Strong match',
        [
          { name: 'Skills', score: 95 },
          { name: 'Experience', score: 88 },
          { name: 'Education', score: 90 },
        ],
        ['Vue.js experience aligns well', 'Strong full-stack background'],
        ['Add more payments-domain keywords']
      ),
    },
    {
      _id: 'dev-job-002',
      title: 'Staff Software Engineer',
      companyName: 'Notion',
      location: 'New York, NY',
      description: 'Lead technical initiatives across collaborative editing surfaces.',
      url: 'https://example.com/jobs/notion-staff',
      sourceSite: 'Indeed',
      createdAt: daysAgo(3),
      status: 'applied',
      hasResume: true,
      salary: '$190K–$240K',
      jobType: 'Full-time',
      notes: 'Referred by Alex. Follow up next week.',
      compatibilityScore: matchScore(
        78,
        'Good match',
        [
          { name: 'Skills', score: 82 },
          { name: 'Experience', score: 74 },
          { name: 'Education', score: 80 },
        ],
        ['Leadership examples in prior roles'],
        ['Highlight distributed systems experience']
      ),
    },
    {
      _id: 'dev-job-003',
      title: 'Product Engineer',
      companyName: 'Linear',
      location: 'Remote',
      description: 'Ship fast, high-quality product features end to end.',
      url: 'https://example.com/jobs/linear-pe',
      sourceSite: 'Web',
      createdAt: daysAgo(5),
      status: 'saved',
      hasResume: false,
      salary: '$150K–$185K',
      jobType: 'Full-time',
      notes: '',
      compatibilityScore: matchScore(
        64,
        'Fair match',
        [
          { name: 'Skills', score: 70 },
          { name: 'Experience', score: 58 },
          { name: 'Education', score: 72 },
        ],
        ['Product sense from side projects'],
        ['Emphasize startup velocity', 'Add design collaboration examples']
      ),
    },
    {
      _id: 'dev-job-004',
      title: 'Machine Learning Engineer',
      companyName: 'Anthropic',
      location: 'San Francisco, CA',
      description: 'Improve model tooling and evaluation pipelines.',
      url: 'https://example.com/jobs/anthropic-mle',
      sourceSite: 'LinkedIn',
      createdAt: daysAgo(8),
      status: 'interviewing',
      hasResume: true,
      salary: '$200K–$280K',
      jobType: 'Full-time',
      notes: 'Phone screen went well. Onsite scheduled.',
      compatibilityScore: matchScore(
        55,
        'Stretch role',
        [
          { name: 'Skills', score: 48 },
          { name: 'Experience', score: 52 },
          { name: 'Education', score: 70 },
        ],
        ['Python and API experience'],
        ['Limited ML production experience', 'Add NLP project details']
      ),
    },
    {
      _id: 'dev-job-005',
      title: 'Full Stack Developer',
      companyName: 'Shopify',
      location: 'Toronto, ON (Hybrid)',
      description: 'Merchant-facing admin tools in React and Ruby.',
      url: 'https://example.com/jobs/shopify-fs',
      sourceSite: 'Glassdoor',
      createdAt: daysAgo(12),
      status: 'saved',
      hasResume: true,
      salary: '$130K–$160K',
      jobType: 'Full-time',
      notes: '',
      compatibilityScore: matchScore(
        86,
        'Strong match',
        [
          { name: 'Skills', score: 90 },
          { name: 'Experience', score: 84 },
          { name: 'Education', score: 82 },
        ],
        ['E-commerce adjacent experience'],
        []
      ),
    },
    {
      _id: 'dev-job-006',
      title: 'DevOps Engineer',
      companyName: 'Datadog',
      location: 'Boston, MA',
      description: 'Own CI/CD, observability, and infrastructure automation.',
      url: 'https://example.com/jobs/datadog-devops',
      sourceSite: 'Indeed',
      createdAt: daysAgo(14),
      status: 'rejected',
      hasResume: true,
      salary: '$145K–$175K',
      jobType: 'Full-time',
      notes: 'Passed on after final round.',
      compatibilityScore: matchScore(
        41,
        'Weak match',
        [
          { name: 'Skills', score: 38 },
          { name: 'Experience', score: 40 },
          { name: 'Education', score: 55 },
        ],
        ['Some AWS exposure'],
        ['Limited Kubernetes depth', 'Add Terraform examples']
      ),
    },
    {
      _id: 'dev-job-007',
      title: 'UX Engineer',
      companyName: 'Figma',
      location: 'Remote',
      description: 'Bridge design and engineering on design-system components.',
      url: 'https://example.com/jobs/figma-uxe',
      sourceSite: 'LinkedIn',
      createdAt: daysAgo(2),
      status: 'saved',
      hasResume: false,
      salary: '$155K–$195K',
      jobType: 'Contract',
      notes: '',
      compatibilityScore: {
        overallVerdict: 'Score pending',
      },
    },
    {
      _id: 'dev-job-008',
      title: 'Backend Engineer, Payments',
      companyName: 'Square',
      location: 'Austin, TX',
      description: 'High-volume payment APIs and ledger services.',
      url: 'https://example.com/jobs/square-backend',
      sourceSite: 'Web',
      createdAt: daysAgo(20),
      status: 'offer',
      hasResume: true,
      salary: '$175K–$210K',
      jobType: 'Full-time',
      notes: 'Offer received – comparing packages.',
      compatibilityScore: matchScore(
        88,
        'Strong match',
        [
          { name: 'Skills', score: 86 },
          { name: 'Experience', score: 91 },
          { name: 'Education', score: 85 },
        ],
        ['API design experience', 'Fintech-adjacent work'],
        []
      ),
    },
  ];
}

export const MOCK_RESUME_PREVIEW = {
  hasResume: true,
  html: `
    <div style="font-family: Georgia, serif; padding: 32px; color: #1a1a2e; max-width: 640px;">
      <h1 style="margin: 0 0 4px; font-size: 28px;">Nicolas Barrionuevo</h1>
      <p style="margin: 0 0 20px; color: #64748b;">dev.reviewer@resymaid.local · +1 (555) 010-2030</p>
      <h2 style="font-size: 16px; text-transform: uppercase; letter-spacing: 1px; color: #5b21b6;">Summary</h2>
      <p>Full-stack engineer with experience building polished Vue dashboards and AI-assisted workflows.</p>
      <h2 style="font-size: 16px; text-transform: uppercase; letter-spacing: 1px; color: #5b21b6;">Experience</h2>
      <p><strong>Senior Software Engineer</strong> — Example Corp (2020 – Present)</p>
      <ul><li>Led frontend architecture for a job-application platform.</li><li>Shipped theme-aware UI across mobile and desktop.</li></ul>
      <h2 style="font-size: 16px; text-transform: uppercase; letter-spacing: 1px; color: #5b21b6;">Skills</h2>
      <p>JavaScript, Vue.js, TypeScript, Node.js, Python, AWS</p>
    </div>
  `,
  parsedData: {
    headline: 'Senior Software Engineer',
    summary: 'Full-stack engineer focused on delightful, accessible product UI.',
    experience: [
      {
        title: 'Senior Software Engineer',
        company: 'Example Corp',
        location: 'Remote',
        duration: '2020 – Present',
        description: 'Led frontend for AI resume tooling.',
        bullets: ['Vue 3 migration', 'Design system adoption'],
        isCurrent: true,
      },
    ],
    education: [
      { degree: 'B.S. Computer Science', institution: 'Example University', year: '2018' },
    ],
    skills: ['JavaScript', 'Vue.js', 'TypeScript', 'Node.js', 'Python', 'AWS'],
  },
};

export const MOCK_REFERRAL = {
  referralCode: 'NICOLAS42',
  stats: {
    totalReferrals: 3,
    pendingReferrals: 1,
    qualifiedReferrals: 2,
    totalCreditsEarned: 20,
  },
};

export const MOCK_PRICES = {
  prices: [
    { id: 'price_dev_starter', name: 'Starter', unit_amount: 999, credits: 10 },
    { id: 'price_dev_standard', name: 'Standard', unit_amount: 2499, credits: 30 },
    { id: 'price_dev_pro', name: 'Pro', unit_amount: 4999, credits: 75 },
  ],
};

function weekStart(d) {
  const x = new Date(d);
  const day = x.getDay();
  const diff = day === 0 ? 6 : day - 1;
  x.setDate(x.getDate() - diff);
  return x.toISOString().slice(0, 10);
}

function mockWeeklySeries(weeks, base, variance) {
  const out = [];
  for (let i = weeks - 1; i >= 0; i--) {
    const d = new Date();
    d.setDate(d.getDate() - i * 7);
    out.push({
      week: weekStart(d),
      count: Math.max(0, base + Math.round((Math.random() - 0.4) * variance)),
    });
  }
  return out;
}

/** Sample admin analytics payloads for dev bypass UI review. */
export function createMockAdminOverview() {
  return {
    generatedAt: new Date().toISOString(),
    users: {
      total: 1284,
      signups7d: 47,
      signups30d: 186,
      mau: 412,
      withBaseResume: 891,
      activationRate: 69.4,
    },
    engagement: {
      totalJobsSaved: 5420,
      resumesGenerated: 3891,
      usersWhoGenerated: 756,
      avgResumesPerActiveUser: 5.1,
      totalTokensUsed: 2840000,
      jobsWithTailoredResume: 3891,
      avgCompatibilityScore: 78.6,
    },
    funnel: {
      byStatus: {
        saved: 980,
        resume_generated: 2100,
        applied: 1420,
        interviewing: 312,
        offer: 48,
        rejected: 560,
      },
      appliedOrBeyond: 2340,
      applicationRate: 60.1,
    },
    referrals: {
      byStatus: { pending: 42, qualified: 28, rewarded: 24, expired: 11 },
      paidConversions: 52,
    },
    topJobSources: [
      { source: 'LinkedIn', count: 4120 },
      { source: 'Indeed', count: 680 },
      { source: 'Glassdoor', count: 320 },
      { source: 'Company site', count: 180 },
    ],
  };
}

export function createMockAdminGrowth(weeks = 12) {
  return {
    weeks,
    signups: mockWeeklySeries(weeks, 14, 10),
    resumeGenerations: mockWeeklySeries(weeks, 85, 40),
    jobsSaved: mockWeeklySeries(weeks, 120, 50),
  };
}

export function createMockSuccessCandidates() {
  return {
    count: 4,
    candidates: [
      {
        userId: 'cand-001',
        email: 'alex.morgan@example.com',
        firstName: 'Alex',
        lastName: 'Morgan',
        phone: '+1 (415) 555-0142',
        headline: 'Product Manager · B2B SaaS',
        signupDate: daysAgo(90),
        lastLogin: daysAgo(2),
        referralCount: 2,
        credits: 8,
        jobCount: 12,
        resumesGenerated: 11,
        appliedCount: 8,
        interviewingCount: 3,
        offerCount: 1,
        rejectedCount: 2,
        lastJobActivity: daysAgo(1),
        lastResumeGenerated: daysAgo(1),
        avgMatchScore: 82.4,
        sampleJobTitles: ['Senior PM', 'Group PM', 'Director of Product'],
        successScore: 231,
        followUpPriority: 'high',
      },
      {
        userId: 'cand-002',
        email: 'jordan.lee@example.com',
        firstName: 'Jordan',
        lastName: 'Lee',
        phone: '',
        headline: 'Software Engineer',
        signupDate: daysAgo(45),
        lastLogin: daysAgo(5),
        referralCount: 0,
        credits: 3,
        jobCount: 7,
        resumesGenerated: 7,
        appliedCount: 5,
        interviewingCount: 2,
        offerCount: 0,
        rejectedCount: 1,
        lastJobActivity: daysAgo(3),
        lastResumeGenerated: daysAgo(4),
        avgMatchScore: 76.1,
        sampleJobTitles: ['Staff Engineer', 'Backend Engineer'],
        successScore: 127,
        followUpPriority: 'high',
      },
      {
        userId: 'cand-003',
        email: 'sam.patel@example.com',
        firstName: 'Sam',
        lastName: 'Patel',
        phone: '+1 (212) 555-0199',
        headline: 'Marketing Manager',
        signupDate: daysAgo(30),
        lastLogin: daysAgo(7),
        referralCount: 1,
        credits: 12,
        jobCount: 4,
        resumesGenerated: 4,
        appliedCount: 3,
        interviewingCount: 0,
        offerCount: 0,
        rejectedCount: 0,
        lastJobActivity: daysAgo(8),
        lastResumeGenerated: daysAgo(9),
        avgMatchScore: 71.0,
        sampleJobTitles: ['Growth Marketing Lead'],
        successScore: 64,
        followUpPriority: 'medium',
      },
      {
        userId: 'cand-004',
        email: 'riley.chen@example.com',
        firstName: 'Riley',
        lastName: 'Chen',
        phone: '',
        headline: 'Data Analyst',
        signupDate: daysAgo(14),
        lastLogin: daysAgo(1),
        referralCount: 0,
        credits: 15,
        jobCount: 3,
        resumesGenerated: 3,
        appliedCount: 0,
        interviewingCount: 0,
        offerCount: 0,
        rejectedCount: 0,
        lastJobActivity: daysAgo(2),
        lastResumeGenerated: daysAgo(2),
        avgMatchScore: 74.5,
        sampleJobTitles: ['Senior Data Analyst', 'BI Analyst'],
        successScore: 3,
        followUpPriority: 'medium',
      },
    ],
  };
}
